import {motion} from "framer-motion";
import { AlertCircle, FileText, FolderOpen, Image, Mail, MessageSquare, Send, Ticket, Upload, X } from "lucide-react";
import { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";

const MAX_FILES = 3;

const TicketForm = () => {
  const [formData, setFormData] = useState({
    title: '',
    email: '',
    priority: '',
    category: '',
    description: '',
    images: []
  });
  const [previews, setPreviews] = useState([]);

  // Cleanup previews when component unmounts
  useEffect(() => {
    return () => {
      previews.forEach(preview => URL.revokeObjectURL(preview.url));
    };
  }, [previews]);

  const onDrop = useCallback((acceptedFiles) => {
    const remainingSlots = MAX_FILES - formData.images.length;
    const filesToAdd = acceptedFiles.slice(0, remainingSlots);

    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...filesToAdd]
    }));
    
    // Create preview URLs for new files
    const newPreviews = filesToAdd.map(file => ({
      file,
      url: URL.createObjectURL(file)
    }));
    
    setPreviews(prev => [...prev, ...newPreviews]);
  }, [formData.images]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    maxSize: 10485760, // 10MB
    multiple: true,
    maxFiles: MAX_FILES,
    disabled: formData.images.length >= MAX_FILES
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Ticket Data:', formData);
  };

  const removeImage = (index) => {
    // Revoke the URL for the removed preview
    URL.revokeObjectURL(previews[index].url);
    
    // Remove the image from both formData and previews
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 mx-auto max-w-4xl"
    >
      {/* Header Section */}
      <div className="mb-10 text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", bounce: 0.5 }}
          className="flex justify-center items-center mx-auto mb-4 w-20 h-20 bg-gradient-to-tr from-purple-600 to-pink-500 rounded-xl transform rotate-12"
        >
          <Image className="w-10 h-10 text-white transform -rotate-12" />
        </motion.div>
        <h2 className="text-2xl font-bold text-gray-800">Create Support Ticket</h2>
        <p className="mt-2 text-gray-600">We'll get back to you within 2 hours</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Issue Title */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-2"
          >
            <label className="flex gap-2 items-center text-sm font-medium text-gray-700">
              <FileText className="w-4 h-4 text-purple-500" />
              Issue Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-shadow focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Enter a clear title for your issue"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              required
            />
          </motion.div>

          {/* Email */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-2"
          >
            <label className="flex gap-2 items-center text-sm font-medium text-gray-700">
              <Mail className="w-4 h-4 text-purple-500" />
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-shadow focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="your@email.com"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
            />
          </motion.div>

          {/* Priority Level */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-2"
          >
            <label className="flex gap-2 items-center text-sm font-medium text-gray-700">
              <AlertCircle className="w-4 h-4 text-purple-500" />
              Priority Level <span className="text-red-500">*</span>
            </label>
            <select
              className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-shadow focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={formData.priority}
              onChange={(e) => setFormData({...formData, priority: e.target.value})}
              required
            >
              <option value="">Select Priority</option>
              <option value="low">Low - Minor Issue</option>
              <option value="medium">Medium - Significant Issue</option>
              <option value="high">High - Critical Issue</option>
              <option value="urgent">Urgent - Emergency</option>
            </select>
          </motion.div>

          {/* Category */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-2"
          >
            <label className="flex gap-2 items-center text-sm font-medium text-gray-700">
              <FolderOpen className="w-4 h-4 text-purple-500" />
              Category <span className="text-red-500">*</span>
            </label>
            <select
              className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-shadow focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              required
            >
              <option value="">Select Category</option>
              <option value="technical">Technical Issue</option>
              <option value="billing">Billing Problem</option>
              <option value="account">Account Access</option>
              <option value="feature">Feature Request</option>
              <option value="other">Other</option>
            </select>
          </motion.div>
        </div>

        {/* Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="space-y-2"
        >
          <label className="flex gap-2 items-center text-sm font-medium text-gray-700">
            <MessageSquare className="w-4 h-4 text-purple-500" />
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            className="px-4 py-3 w-full h-40 rounded-lg border border-gray-300 transition-shadow resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="Please describe your issue in detail..."
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            required
          />
        </motion.div>

        {/* File Upload with Preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="space-y-2"
        >
          <label className="flex gap-2 items-center text-sm font-medium text-gray-700">
            <Image className="w-4 h-4 text-purple-500" />
            Attach Screenshots ({formData.images.length}/{MAX_FILES})
          </label>
          <div className="flex flex-col gap-4">
            {formData.images.length < MAX_FILES && (
              <div
                {...getRootProps()}
                className={`flex justify-center items-center w-full h-32 rounded-lg border-2 border-dashed transition-colors cursor-pointer
                  ${isDragActive ? 'bg-purple-50 border-purple-500' : 'border-gray-300 hover:bg-gray-50'}`}
              >
                <input {...getInputProps()} />
                <div className="flex flex-col justify-center items-center pt-5 pb-6">
                  <Upload className="mb-2 w-8 h-8 text-gray-400" />
                  <p className="text-sm text-gray-500">
                    {isDragActive
                      ? "Drop the images here"
                      : "Drop your files here or click to upload"}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    PNG, JPG up to 10MB (max {MAX_FILES} images)
                  </p>
                </div>
              </div>
            )}

            {/* Image Previews */}
            {previews.length > 0 && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                {previews.map((preview, index) => (
                  <div key={preview.url} className="relative group">
                    <img
                      src={preview.url}
                      alt={`Preview ${index + 1}`}
                      className="object-cover w-full h-48 rounded-lg"
                    />
                    <motion.button
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      type="button"
                      onClick={() => removeImage(index)}
                      className="flex absolute inset-0 justify-center items-center w-full h-full text-white bg-black bg-opacity-50 rounded-lg opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      <X className="w-6 h-6" />
                    </motion.button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* Submit Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="flex justify-end"
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex gap-2 items-center px-8 py-3 font-medium text-white bg-gradient-to-r from-purple-600 to-pink-500 rounded-lg shadow-lg shadow-purple-500/30"
            type="submit"
          >
            <Send className="w-4 h-4" />
            Submit Ticket
          </motion.button>
        </motion.div>
      </form>
    </motion.div>
  );
};

export default TicketForm;